import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { timestampToIso } from './customersService'
import { stripUndefined } from '../utils/firestore'
import { trackWrite } from '../utils/syncTracker'
import type { Case } from '../types/admin'

export const casesRef = collection(db, 'cases')

function toCase(snapshot: QueryDocumentSnapshot<DocumentData>): Case {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    number: data.number ?? '',
    customerId: data.customerId ?? '',
    customerName: data.customerName ?? '',
    title: data.title ?? '',
    address: data.address ?? '',
    description: data.description ?? '',
    category: data.category ?? 'Multiservice',
    priceModel: data.priceModel ?? 'Tilbud endnu ikke lavet',
    hourlyRate: data.hourlyRate ?? '',
    fixedPrice: data.fixedPrice ?? '',
    estimate: data.estimate ?? '',
    finalAmount: data.finalAmount ?? '',
    plannedDate: data.plannedDate ?? '',
    status: data.status ?? 'Ny henvendelse',
    internalNotes: Array.isArray(data.internalNotes) ? data.internalNotes : [],
    photos: Array.isArray(data.photos) ? data.photos : [],
    createdAt: timestampToIso(data.createdAt),
    updatedAt: timestampToIso(data.updatedAt),
    fromWebsite: Boolean(data.fromWebsite),
    enquiryId: data.enquiryId ?? undefined,
    quoteId: data.quoteId ?? undefined,
    quoteNumber: data.quoteNumber ?? undefined,
    quoteLines: Array.isArray(data.quoteLines) ? data.quoteLines : undefined,
    invoiceId: data.invoiceId || undefined,
    invoiceNumber: data.invoiceNumber || undefined,
    entries: Array.isArray(data.entries) ? data.entries : [],
    beforeImages: Array.isArray(data.beforeImages) ? data.beforeImages : [],
    duringImages: Array.isArray(data.duringImages) ? data.duringImages : [],
    afterImages: Array.isArray(data.afterImages) ? data.afterImages : [],
  }
}

export function subscribeCases(
  onChange: (cases: Case[]) => void,
  onError: (error: unknown) => void,
): () => void {
  return onSnapshot(
    query(casesRef, orderBy('createdAt', 'desc')),
    (snapshot) => onChange(snapshot.docs.map(toCase)),
    onError,
  )
}

export async function createCaseDoc(
  data: Omit<Case, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const created = await addDoc(casesRef, {
    ...stripUndefined(data as unknown as Record<string, unknown>),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return created.id
}

export async function updateCaseDoc(id: string, patch: Partial<Case>): Promise<void> {
  // Løftet afsluttes først, når serveren har bekræftet — så "Gemt" aldrig vises for tidligt.
  await trackWrite(
    updateDoc(doc(db, 'cases', id), {
      ...stripUndefined(patch as Record<string, unknown>),
      updatedAt: serverTimestamp(),
    }),
  )
}

export async function deleteCaseDoc(id: string): Promise<void> {
  await deleteDoc(doc(db, 'cases', id))
}
